import { z } from "zod";
import { customActionIdSchema } from "./ids.js";

export const INPUT_TYPES = ["string", "number", "boolean", "email", "text"] as const;
export type InputType = (typeof INPUT_TYPES)[number];

export const INPUT_SOURCES = ["extract", "compose", "constant", "template"] as const;
export type InputSource = (typeof INPUT_SOURCES)[number];

export const ACTION_TYPES = ["reply", "forward", "webhook", "hold", "ignore"] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

export const ACTION_MODES = ["auto", "approve"] as const;
export type ActionMode = (typeof ACTION_MODES)[number];

/**
 * Every variable a `template` input can use. `email: true` marks the variables
 * that always hold an email address, so a template input of type `email` can be
 * checked at save time.
 */
export const TEMPLATE_VARIABLES = {
  "sender.email": { email: true, description: "The sender's address" },
  "sender.name": { email: false, description: "The sender's display name, or empty" },
  "message.id": { email: false, description: "The message id (m_...)" },
  "message.subject": { email: false, description: "The subject" },
  "message.text": { email: false, description: "The cleaned text, capped at 8,000 chars" },
  "message.receivedAt": { email: false, description: "When the message arrived (ISO 8601)" },
  "conversation.id": { email: false, description: "The conversation id (c_...)" },
  "inbox.address": { email: true, description: "The forward address the message arrived at" },
  "inbox.replyTo": { email: true, description: "Your real support address for that inbox" },
  "tenant.name": { email: false, description: "Your business name" },
  "decision.reason": { email: false, description: "The one-sentence reason for the route" },
} as const;
export type TemplateVariable = keyof typeof TEMPLATE_VARIABLES;

export function isTemplateVariable(name: string): name is TemplateVariable {
  return Object.hasOwn(TEMPLATE_VARIABLES, name);
}

/** Matches one `{{ name }}` token. Global, so use it with `matchAll` or `replace`. */
export const TEMPLATE_TOKEN = /\{\{\s*([a-zA-Z.]+)\s*\}\}/g;

export const inputNameSchema = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,31}$/);

const inputBase = {
  name: inputNameSchema,
  type: z.enum(INPUT_TYPES),
  required: z.boolean(),
  description: z.string().max(200).optional(),
};

export const extractInputSchema = z.strictObject({ ...inputBase, source: z.literal("extract") });
export const composeInputSchema = z.strictObject({ ...inputBase, type: z.literal("text"), source: z.literal("compose") });
export const constantInputSchema = z.strictObject({ ...inputBase, source: z.literal("constant"), value: z.string().min(1).max(2000) });
export const templateInputSchema = z.strictObject({ ...inputBase, source: z.literal("template"), value: z.string().min(1).max(2000) });

export const actionInputSchema = z.discriminatedUnion("source", [
  extractInputSchema,
  composeInputSchema,
  constantInputSchema,
  templateInputSchema,
]);

export type ActionInput = z.infer<typeof actionInputSchema>;
export type ExtractInput = z.infer<typeof extractInputSchema>;
export type ComposeInput = z.infer<typeof composeInputSchema>;

export const replyWithSchema = z.union([
  z.literal("none"),
  z.literal("response"),
  z.templateLiteral(["input:", inputNameSchema]),
]);
export type ReplyWith = z.infer<typeof replyWithSchema>;

export const webhookTargetSchema = z.strictObject({
  url: z.url({ protocol: /^https$/ }).max(2000),
  timeoutMs: z.int().min(1000).max(25_000),
});

const actionCommon = {
  name: z.string().trim().min(1).max(40),
  description: z.string().trim().min(1).max(500),
  enabled: z.boolean(),
  /** When a run of this action finishes, the message goes on the follow-up list. */
  followUp: z.boolean(),
};

const runnableCommon = {
  ...actionCommon,
  mode: z.enum(ACTION_MODES),
  dailyCap: z.int().min(1).max(10_000),
};

/** The reply itself: the writer fills it and the run sends it. */
export const replyTextInputSchema = composeInputSchema.extend({ name: z.literal("text"), required: z.literal(true) });

const forwardToFields = { name: z.literal("to"), type: z.literal("email"), required: z.literal(true) };
/** Where a forward goes. A fixed value or a template, so the router cannot change it. */
export const forwardToInputSchema = z.discriminatedUnion("source", [
  constantInputSchema.extend(forwardToFields),
  templateInputSchema.extend(forwardToFields),
]);

/** One line the writer puts above the forwarded message. */
export const forwardSummaryInputSchema = composeInputSchema.extend({ name: z.literal("summary") });

// Reply and forward runs read only these inputs, so the spec allows no others.
const replySpec = z.strictObject({ type: z.literal("reply"), ...runnableCommon, inputs: z.tuple([replyTextInputSchema]) });
const forwardSpec = z.strictObject({
  type: z.literal("forward"),
  ...runnableCommon,
  inputs: z.union([z.tuple([forwardToInputSchema]), z.tuple([forwardToInputSchema, forwardSummaryInputSchema])]),
});
const webhookSpec = z.strictObject({
  type: z.literal("webhook"),
  ...runnableCommon,
  inputs: z.array(actionInputSchema).max(10),
  webhook: webhookTargetSchema,
  afterRun: z.strictObject({ replyWith: replyWithSchema }),
});
// Only a run opens a follow-up. A held message already waits for a member, and an ignored one needs no work.
const holdSpec = z.strictObject({ type: z.literal("hold"), ...actionCommon, enabled: z.literal(true), followUp: z.literal(false) });
const ignoreSpec = z.strictObject({ type: z.literal("ignore"), ...actionCommon, followUp: z.literal(false) });

const specVariants = [replySpec, forwardSpec, webhookSpec, holdSpec, ignoreSpec] as const;

function applyActionRules(spec: ActionSpec, ctx: z.RefinementCtx): void {
  for (const issue of actionRuleIssues(spec)) ctx.addIssue({ code: "custom", message: issue.message, path: issue.path });
}

/**
 * One action, without its id: the body of `PUT /actions/{id}`. The rules that
 * span fields (unique input names, template variables, constant values) are in
 * `actionRuleIssues`.
 */
export const actionSpecSchema = z.discriminatedUnion("type", specVariants).superRefine(applyActionRules);
export type ActionSpec = z.infer<typeof actionSpecSchema>;

/** An action as stored and as the control API returns it. The webhook secret is never part of it. */
export const actionSchema = z
  .discriminatedUnion("type", [
    replySpec.extend({ id: customActionIdSchema }),
    forwardSpec.extend({ id: customActionIdSchema }),
    webhookSpec.extend({ id: customActionIdSchema }),
    holdSpec.extend({ id: z.literal("hold") }),
    ignoreSpec.extend({ id: z.literal("ignore") }),
  ])
  .superRefine(applyActionRules);
export type Action = z.infer<typeof actionSchema>;

export type RunnableAction = Extract<Action, { type: "reply" | "forward" | "webhook" }>;
export type WebhookAction = Extract<Action, { type: "webhook" }>;

/** Body of `POST /actions`. A webhook action also carries its secret, once. */
export const actionCreateSchema = z.object({
  spec: actionSpecSchema,
  webhookSecret: z.string().min(16).max(128).optional(),
}).superRefine((body, ctx) => {
  if (body.spec.type === "webhook" && body.webhookSecret === undefined) {
    ctx.addIssue({ code: "custom", message: "A webhook action needs a secret", path: ["webhookSecret"] });
  }
  if (body.spec.type !== "webhook" && body.webhookSecret !== undefined) {
    ctx.addIssue({ code: "custom", message: "Only a webhook action takes a secret", path: ["webhookSecret"] });
  }
});
export type ActionCreate = z.infer<typeof actionCreateSchema>;

/**
 * A starting point for a new action, not a valid action yet: the webhook URL
 * and fixed values are blank, so the tenant must fill them before saving.
 */
const draftInputSchema = z.discriminatedUnion("source", [
  extractInputSchema,
  composeInputSchema,
  constantInputSchema.extend({ value: z.string().max(2000) }),
  templateInputSchema.extend({ value: z.string().max(2000) }),
]);

export const actionTemplateSpecSchema = z.strictObject({
  type: z.enum(["reply", "forward", "webhook"]),
  name: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  followUp: z.boolean(),
  mode: z.enum(ACTION_MODES),
  dailyCap: z.int(),
  inputs: z.array(draftInputSchema),
  webhook: z.strictObject({ url: z.string(), timeoutMs: z.int() }).optional(),
  afterRun: z.strictObject({ replyWith: replyWithSchema }).optional(),
});
export type ActionTemplateSpec = z.infer<typeof actionTemplateSpecSchema>;

export const rotateSecretSchema = z.strictObject({ secret: z.string().min(16).max(128) });

interface RuleIssue {
  message: string;
  path: (string | number)[];
}

/** The cross-field rules for an action spec. An empty list means the spec is valid. */
export function actionRuleIssues(spec: ActionSpec): RuleIssue[] {
  if (spec.type === "hold" || spec.type === "ignore") return [];

  const issues: RuleIssue[] = [];
  const names = new Set<string>();
  spec.inputs.forEach((input, index) => {
    if (names.has(input.name)) issues.push({ message: `Input name "${input.name}" is used twice`, path: ["inputs", index, "name"] });
    names.add(input.name);
    issues.push(...inputValueIssues(input, index));
  });

  if (spec.type === "webhook" && spec.afterRun.replyWith.startsWith("input:")) {
    const inputName = spec.afterRun.replyWith.slice("input:".length);
    if (!names.has(inputName)) issues.push({ message: `replyWith names an input that does not exist: ${inputName}`, path: ["afterRun", "replyWith"] });
  }
  return issues;
}

function inputValueIssues(input: ActionInput, index: number): RuleIssue[] {
  const path = ["inputs", index, "value"];
  if (input.source === "constant") {
    const problem = constantProblem(input.type, input.value);
    return problem === null ? [] : [{ message: problem, path }];
  }
  if (input.source === "template") {
    const problem = templateProblem(input.type, input.value);
    return problem === null ? [] : [{ message: problem, path }];
  }
  return [];
}

function constantProblem(type: InputType, value: string): string | null {
  if (type === "number" && !Number.isFinite(Number(value))) return `"${value}" is not a number`;
  if (type === "boolean" && value !== "true" && value !== "false") return 'A yes/no value must be "true" or "false"';
  if (type === "email" && !z.email().safeParse(value).success) return `"${value}" is not an email address`;
  return null;
}

function templateProblem(type: InputType, value: string): string | null {
  const used = [...value.matchAll(TEMPLATE_TOKEN)].map((match) => match[1] ?? "");
  const unknown = used.find((name) => !isTemplateVariable(name));
  if (unknown !== undefined) return `Unknown template variable {{${unknown}}}`;
  if (type === "number" || type === "boolean") return "A template input must be text or an email";
  if (type === "email") {
    const single = used.length === 1 && value.trim() === `{{${used[0]}}}`;
    const variable = used[0];
    if (!single || variable === undefined || !isTemplateVariable(variable) || !TEMPLATE_VARIABLES[variable].email) {
      return "An email template must be exactly one email variable, such as {{sender.email}}";
    }
  }
  return null;
}
